import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../../../ui/components/Toast';
import { getAccessToken } from '../../../../../../../util/auth.util';
import { getCurrentCompanyId } from '../../../../../../../util/token.util';
import { Order } from '../../../../../../order';

interface UnileverImportScanModalUploadPaneProps {
  file: File;

  order: Order;

  handleClose: () => void;
}

export default function UnileverImportScanModalUploadPane({
  handleClose,
  file,
  order,
}: UnileverImportScanModalUploadPaneProps) {
  const executed = useRef(false);
  const { showToast } = useToast();

  const [progress, setProgress] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [total, setTotal] = useState(0);

  const uploadFile = async () => {
    const xhr = new XMLHttpRequest();

    xhr.open('POST', `${process.env.REACT_APP_API_BASE_URL}/api/unilever/scan/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${getAccessToken()}`);
    xhr.setRequestHeader('x-company', getCurrentCompanyId() || '');

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 3) {
        if (xhr.status >= 200 && xhr.status < 300) {
          const lines = xhr.responseText.trim().split('\n');
          const last = JSON.parse(lines[lines.length - 1]);

          if (last.parsed && last.total) {
            setProgress(parseInt(last.parsed));
            setPercentage(Math.round((last.parsed / last.total) * 100));
            setTotal(parseInt(last.total));
          }
        } else {
          showToast({
            type: ToastType.error,
            title: 'Upload failed.',
            content: xhr.response || 'An error occurred during upload.',
          });
          handleClose();
        }
      } else if (xhr.readyState === 4 && xhr.status === 200) {
        const lines = xhr.responseText.trim().split('\n');
        const last = JSON.parse(lines[lines.length - 1]);

        if (last.error) {
          showToast({
            type: ToastType.error,
            title: 'Upload failed.',
            content: last.error || 'An error occurred during upload.',
          });
          handleClose();
          return;
        }

        showToast({
          type: ToastType.success,
          title: 'Upload successful',
          content: `File uploaded successfully`,
        });
        handleClose();
      }
    };

    xhr.onerror = () => {
      showToast({
        type: ToastType.error,
        title: 'Network error.',
        content: 'A network error occurred during upload.',
      });
    };

    const data = new FormData();
    data.append('file', file);
    data.append('name', file.name);
    data.append('type', file.type);
    data.append('id', order.id);

    try {
      xhr.send(data);
    } catch (error) {
      showToast({
        type: ToastType.error,
        title: 'An error occurred while uploading the file.',
      });
    }
  };

  useEffect(() => {
    if (executed.current) return;
    executed.current = true;

    uploadFile();
  }, []);

  return (
    <div className='flex h-full flex-col gap-2'>
      <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
        <p className='title'>{'Importing scanned items'}</p>

        <progress
          value={percentage}
          max='100'
          className='h-2 w-[400px] [&::-moz-progress-bar]:bg-[var(--primary-color)] [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-[var(--primary-color)]'
        />
        <p className='subtitle'>{`${percentage}%`}</p>
      </div>
      <div className='flex w-full justify-center'>
        <p className='subtitle text-xs'>{`(${progress}/${total})`}</p>
      </div>
    </div>
  );
}
